import { useMutation, useQueryClient } from 'react-query';
import {
	updateOTFeedback,
	deleteOTFeedback,
} from '../../../apiCalls/member/occupationalTherapist';
import { QueryKey } from '../../../QueryKeys';

/*
 * This hook returns 2 functions:
 * Function 1: submitFeedback for submitting the feedback selection.
 * Function 2: resetFeedback for resetting the feedback to null so the user can change their initial answer.
 */
const useOTSubmitFeedback = (apiEnv, ssId) => {
	const queryClient = useQueryClient();
	const queryKey = [QueryKey.GET_OT_PROGRAM_DETAILS, apiEnv, ssId];

	// This function is for submitting the feedback selection. It's using optimistic updates to enhance performance.
	const { mutate: submitFeedback } = useMutation(
		data => updateOTFeedback(apiEnv, ssId, data),
		{
			onMutate: async newData => {
				await queryClient.cancelQueries(queryKey); // Cancel this so it doesn't interfere with our optimistic update.

				// Save the old data so we can roll back the changes if the request fails.
				const prevData = queryClient.getQueryData(queryKey);

				// Optimistically update the data under the assumption that the request will
				// succeed. This will make the performance look better to the user.
				queryClient.setQueryData(queryKey, oldData => {
					const newActionItems = oldData?.memberProgramDetail?.actionItems?.map(
						oldActionItem => {
							if (oldActionItem.id === newData.memberActionItemId)
								return {
									...oldActionItem,
									selectedRating: newData.selectedRating,
								};

							return oldActionItem;
						}
					);

					return {
						...oldData,
						memberProgramDetail: {
							...oldData?.memberProgramDetail,
							actionItems: newActionItems,
						},
					};
				});

				return { prevData };
			},
			onError: (_error, _args, context) => {
				// Rollback the mutation if there was an error during the request.
				// TODO: Decide on a nice error message to show when we have to rollback the changes.
				queryClient.setQueryData(queryKey, context.prevData);
			},
		}
	);

	// This function is for resetting the feedback to null so the user can change their initial answer.

	const { mutate: resetFeedback } = useMutation(
		id => deleteOTFeedback(apiEnv, ssId, id),
		{
			onMutate: async newData => {
				// Cancel this so it doesn't interfere with our optimistic update.
				await queryClient.cancelQueries(queryKey);

				// Save the old data so we can roll back the changes if the request fails.
				const prevData = queryClient.getQueryData(queryKey);

				// Optimistically update the data under the assumption that the request will
				// succeed. This will make the performance look better to the user.
				queryClient.setQueryData(queryKey, oldData => {
					const newActionItems = oldData?.memberProgramDetail?.actionItems?.map(
						oldActionItem => {
							if (oldActionItem.id === newData.memberActionItemId)
								return {
									...oldActionItem,
									selectedRating: null,
								};

							return oldActionItem;
						}
					);

					return {
						...oldData,
						memberProgramDetail: {
							...oldData?.memberProgramDetail,
							actionItems: newActionItems,
						},
					};
				});

				return { prevData };
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey,
				});
			},
			onError: (_error, _args, context) => {
				// Rollback the mutation if there was an error during the request.
				// TODO: Decide on a nice error message to show when we have to rollback the changes.
				queryClient.setQueryData(queryKey, context.prevData);
			},
		}
	);

	return { submitFeedback, resetFeedback };
};

export default useOTSubmitFeedback;
